import React, { useEffect, useState } from "react";
import { Card, CardBody, CardHeader, Table, Button } from "reactstrap";
import { Modal, Badge } from "react-bootstrap";
import { FaEdit, FaTrash } from "react-icons/fa";
import { Switch } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useDispatch } from "react-redux";
import EditHealthcareForm from "./EditHealthcareForm";
import healthcarePlaceholderImage from "../assets/img/restaurantimage_unavailable.png";
import { getUserFromLocalStorage } from "utilities/user.js";
import { toast } from "react-toastify";
import * as api from "../api";
import AlertSoundPrefs from "../components/AlertSoundPrefs";
import VendorAlertSettings from "../components/VendorAlertSettings";
import VendorTaxSettings from "../components/VendorTaxSettings";
import StoreOfferCard from "../components/StoreOfferCard";
import { useGate } from "../components/team/Gated";

const useStyles = makeStyles((theme) => ({
  switchBase: {
    color: theme.palette.secondary.main,
    "&$checked": {
      color: theme.palette.secondary.main,
    },
    "&$checked + $track": {
      backgroundColor: theme.palette.secondary.main,
    },
  },
  checked: {},
  track: {},
}));

const HealthcareAccount = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingProfile, setEditingProfile] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const user = getUserFromLocalStorage();
  const vendorId = user?.result?._id;

  useEffect(() => {
    if (vendorId) {
      fetchProfile();
    }
  }, [vendorId]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const { data } = await api.apiFetchHealthcareProfile(vendorId);
      setProfile(data);
      setLoading(false);
    } catch (err) {
      setError("Failed to load profile");
      setLoading(false);
      toast.error("Failed to load healthcare profile");
    }
  };

  const handleDeleteProfile = () => {
    setShowDeleteModal(true);
  };

  const confirmDeleteProfile = async () => {
    try {
      await api.apiDeleteHealthcareVendor(profile._id);
      toast.success("Healthcare vendor deleted successfully");
      setShowDeleteModal(false);
    } catch (error) {
      toast.error("Failed to delete healthcare vendor");
    }
  };

  // Vendor team permissions: saving the vendor profile is settings:edit.
  const settingsGate = useGate("settings:edit");

  const handleEditClick = () => {
    setEditingProfile(profile);
  };

  const handleSave = async (updatedProfile) => {
    try {
      const { data } = await api.apiUpdateHealthcareProfile(vendorId, updatedProfile);
      setProfile(data);
      setEditingProfile(null);
      toast.success("Profile updated successfully!");
      fetchProfile();
    } catch (error) {
      toast.error("Failed to update profile");
    }
  };

  const handleCancel = () => {
    setEditingProfile(null);
  };

  // Store-wide offer (vendor discounts): the sections feed the scope picker.
  const [offerSections, setOfferSections] = useState([]);
  const [savingOffer, setSavingOffer] = useState(false);
  useEffect(() => {
    let alive = true;
    if (!profile?._id) return undefined;
    api
      .apiFetchHealthcareSections(profile._id)
      .then((res) => { if (alive) setOfferSections(Array.isArray(res?.data) ? res.data : []); })
      .catch(() => { if (alive) setOfferSections([]); });
    return () => { alive = false; };
  }, [profile?._id]);
  const handleStoreOfferSave = async (storeOffer) => {
    setSavingOffer(true);
    try {
      const { data } = await api.apiUpdateHealthcareProfile(vendorId, { storeOffer });
      if (data) setProfile((prev) => ({ ...(prev || {}), ...data }));
      toast.success(storeOffer.enabled ? "Store-wide offer saved." : "Store-wide offer ended.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save the offer.");
    } finally {
      setSavingOffer(false);
    }
  };

  const handleActivationToggle = async (isActive) => {
    try {
      await api.apiUpdateHealthcareProfile(vendorId, { isActive });
      setProfile({ ...profile, isActive });
      toast.success(`Healthcare vendor ${isActive ? "activated" : "deactivated"}`);
    } catch (error) {
      toast.error("Failed to update activation status");
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return (
      <div>
        {error}
        <Button onClick={fetchProfile} color="primary" style={{ marginLeft: "10px" }}>
          Retry!
        </Button>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px" }}>
      <Card
        style={{
          borderRadius: "10px",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        }}
      >
        <CardHeader
          style={{
            backgroundColor: "#E91E63",
            color: "#fff",
            borderTopLeftRadius: "10px",
            borderTopRightRadius: "10px",
            textAlign: "center",
          }}
        >
          <h2>Healthcare Provider Profile</h2>
        </CardHeader>
        <CardBody>
          {editingProfile ? (
            <EditHealthcareForm
              healthcare={editingProfile}
              onSave={handleSave}
              onCancel={handleCancel}
            />
          ) : (
            <Table responsive hover striped>
              <thead className="thead-dark">
                <tr>
                  <th style={{ textAlign: "left", verticalAlign: "middle" }}>#</th>
                  <th style={{ textAlign: "left", verticalAlign: "middle" }}>Name</th>
                  <th style={{ textAlign: "center", verticalAlign: "middle" }}>Logo</th>
                  <th style={{ textAlign: "left", verticalAlign: "middle", width: "20%" }}>
                    Location
                  </th>
                  <th style={{ textAlign: "left", verticalAlign: "middle" }}>License</th>
                  <th style={{ textAlign: "left", verticalAlign: "middle" }}>Specializations</th>
                  <th style={{ textAlign: "center", verticalAlign: "middle" }}>
                    Activation
                  </th>
                  <th style={{ textAlign: "center", verticalAlign: "middle" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {profile && (
                  <tr>
                    <td>1</td>
                    <td>{profile.name}</td>
                    <td style={{ textAlign: "center" }}>
                      <img
                        src={profile.imageUrl || healthcarePlaceholderImage}
                        alt={profile.name}
                        style={{
                          width: "60px",
                          height: "60px",
                          objectFit: "cover",
                          borderRadius: "5px",
                        }}
                      />
                    </td>
                    <td>{profile.address}</td>
                    <td>
                      <small>{profile.medicalLicenseNumber || "N/A"}</small>
                    </td>
                    <td>
                      {profile.specializations && profile.specializations.length > 0 ? (
                        profile.specializations.map((spec, idx) => (
                          <Badge key={idx} bg="info" className="me-1">
                            {spec}
                          </Badge>
                        ))
                      ) : (
                        <small className="text-muted">None</small>
                      )}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <Switch
                        checked={profile.isActive}
                        onChange={(e) => handleActivationToggle(e.target.checked)}
                        classes={{
                          switchBase: classes.switchBase,
                          checked: classes.checked,
                          track: classes.track,
                        }}
                      />
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <Button
                        disabled={!settingsGate.allowed}
                        title={settingsGate.title}
                        color="primary"
                        size="sm"
                        onClick={handleEditClick}
                        style={{ marginRight: "5px" }}
                      >
                        <FaEdit /> Edit
                      </Button>
                      <Button
                        color="danger"
                        size="sm"
                        onClick={handleDeleteProfile}
                      >
                        <FaTrash /> Delete
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* Notifications: the shared Alert settings panel (email / SMS / WhatsApp,
          alert contacts, test alert) plus the personal in-app alert sound, the
          same pair the restaurant and shop profiles mount on their
          Notifications tab. */}
      <Card
        style={{
          marginTop: "20px",
          borderRadius: "10px",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        }}
      >
        <CardHeader
          style={{
            backgroundColor: "#E91E63",
            color: "#fff",
            borderTopLeftRadius: "10px",
            borderTopRightRadius: "10px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <i className="fas fa-bell" />
          <h4 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 700 }}>Notifications</h4>
        </CardHeader>
        <CardBody>
          <VendorAlertSettings />
          <div style={{ borderTop: "1px solid #eef1f6", margin: "18px 0 0", paddingTop: 18 }}>
            <AlertSoundPrefs />
          </div>
        </CardBody>
      </Card>

      {/* Tax and receipts — the vendor's own view of §17. Its own card rather
          than a row inside Notifications, because a pharmacy is asked to make a
          decision here about a legal obligation, not to set a preference. */}
      <Card style={{ marginTop: 20 }}>
        <CardHeader
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <i className="fas fa-receipt" />
          <h4 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 700 }}>
            Tax &amp; Receipts
          </h4>
        </CardHeader>
        <CardBody>
          <VendorTaxSettings />
        </CardBody>
      </Card>

      {/* Store-wide offer (vendor discounts) */}
      <StoreOfferCard
        value={profile?.storeOffer}
        sections={offerSections}
        saving={savingOffer}
        onSave={handleStoreOfferSave}
      />

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete your healthcare provider profile? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button color="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button color="danger" onClick={confirmDeleteProfile}>
            Delete Profile
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default HealthcareAccount;






